import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invitationsService } from '@/services/invitations.service';
import {
  Invitation,
  InviteToOrgRequest,
  InviteToBrandSpaceRequest,
  ListInvitationsParams,
  VerifyInvitationResponse,
  AcceptInvitationResponse,
} from '@/types/invitations';

/**
 * React Query hooks for invitations
 *
 * Provides hooks for:
 * - Organization invitations (list, create)
 * - Brand space invitations (list, create)
 * - Invitation management (cancel, resend)
 * - Public invitation actions (verify, accept, decline)
 */

// ============================================================================
// Organization Invitation Hooks
// ============================================================================

/**
 * Fetch all invitations for an organization
 *
 * @param orgId - Organization ID
 * @param params - Optional query parameters (status filter)
 * @returns Query result with organization invitations
 */
export const useOrganizationInvitations = (
  orgId: number,
  params?: ListInvitationsParams
) => {
  return useQuery<Invitation[], Error>({
    queryKey: ['invitations', 'organization', orgId, params],
    queryFn: () => invitationsService.getOrganizationInvitations(orgId, params),
    enabled: !!orgId,
  });
};

/**
 * Invite a user to an organization
 *
 * @returns Mutation object for creating organization invitations
 */
export const useInviteToOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Invitation,
    Error,
    { orgId: number; data: InviteToOrgRequest }
  >({
    mutationFn: ({ orgId, data }) =>
      invitationsService.inviteToOrganization(orgId, data),
    onSuccess: (_, variables) => {
      // Invalidate all invitation lists for this organization
      queryClient.invalidateQueries({
        queryKey: ['invitations', 'organization', variables.orgId],
      });
    },
  });
};

// ============================================================================
// Team Invitation Hooks
// ============================================================================

/**
 * Fetch all invitations for a team
 *
 * @param teamId - Team ID
 * @param params - Optional query parameters (status filter)
 * @returns Query result with team invitations
 */
export const useTeamInvitations = (
  teamId: number,
  params?: ListInvitationsParams
) => {
  return useQuery<Invitation[], Error>({
    queryKey: ['invitations', 'team', teamId, params],
    queryFn: () =>
      invitationsService.getTeamInvitations(teamId, params),
    enabled: !!teamId,
  });
};

/**
 * Invite a user to a team
 *
 * @returns Mutation object for creating team invitations
 */
export const useInviteToTeam = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Invitation,
    Error,
    { teamId: number; data: InviteToBrandSpaceRequest }
  >({
    mutationFn: ({ teamId, data }) =>
      invitationsService.inviteToTeam(teamId, data),
    onSuccess: (_, variables) => {
      // Invalidate all invitation lists for this team
      queryClient.invalidateQueries({
        queryKey: ['invitations', 'team', variables.teamId],
      });
    },
  });
};

// ============================================================================
// Invitation Management Hooks
// ============================================================================

/**
 * Cancel an invitation
 *
 * @returns Mutation object for cancelling invitations
 */
export const useCancelInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { message: string },
    Error,
    { invitationId: number; orgId?: number; brandSpaceId?: number; teamId?: number }
  >({
    mutationFn: ({ invitationId }) =>
      invitationsService.cancelInvitation(invitationId),
    onSuccess: (_, variables) => {
      // Invalidate relevant invitation lists
      if (variables.orgId) {
        queryClient.invalidateQueries({
          queryKey: ['invitations', 'organization', variables.orgId],
        });
      }
      if (variables.brandSpaceId) {
        queryClient.invalidateQueries({
          queryKey: ['invitations', 'brandSpace', variables.brandSpaceId],
        });
      }
      if (variables.teamId) {
        queryClient.invalidateQueries({
          queryKey: ['invitations', 'team', variables.teamId],
        });
      }
    },
  });
};

/**
 * Resend an invitation email
 *
 * @returns Mutation object for resending invitations
 */
export const useResendInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Invitation,
    Error,
    { invitationId: number; orgId?: number; brandSpaceId?: number; teamId?: number }
  >({
    mutationFn: ({ invitationId }) =>
      invitationsService.resendInvitation(invitationId),
    onSuccess: (_, variables) => {
      // Invalidate relevant invitation lists
      if (variables.orgId) {
        queryClient.invalidateQueries({
          queryKey: ['invitations', 'organization', variables.orgId],
        });
      }
      if (variables.brandSpaceId) {
        queryClient.invalidateQueries({
          queryKey: ['invitations', 'brandSpace', variables.brandSpaceId],
        });
      }
      if (variables.teamId) {
        queryClient.invalidateQueries({
          queryKey: ['invitations', 'team', variables.teamId],
        });
      }
    },
  });
};

// ============================================================================
// Public Invitation Hooks (Token-based)
// ============================================================================

/**
 * Verify an invitation token (public - no auth required)
 *
 * Returns invitation details with organization/brand space info
 *
 * @param token - Invitation token (64 hex characters)
 * @param options - Additional query options
 * @returns Query result with invitation details
 */
export const useVerifyInvitation = (
  token: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<VerifyInvitationResponse, Error>({
    queryKey: ['invitations', 'verify', token],
    queryFn: () => invitationsService.verifyToken(token),
    enabled: token.length === 64 && (options?.enabled ?? true),
    retry: 1, // Only retry once for verification
    staleTime: 0, // Always refetch to get fresh invitation status
  });
};

/**
 * Accept an invitation
 *
 * Requires authentication. User email must match invitation email.
 *
 * @returns Mutation object for accepting invitations
 */
export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation<AcceptInvitationResponse, Error, string>({
    mutationFn: (token: string) => invitationsService.acceptInvitation(token),
    onSuccess: () => {
      // Invalidate user data to reflect new memberships
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
};

/**
 * Decline an invitation (public - no auth required)
 *
 * @returns Mutation object for declining invitations
 */
export const useDeclineInvitation = () => {
  return useMutation<{ message: string }, Error, string>({
    mutationFn: (token: string) => invitationsService.declineInvitation(token),
  });
};
